import jwtService from './jwtService';
import { showMessage } from 'app/store/actions';
import { setUserData } from './user.actions';
import decode from 'jwt-decode';
import { API_BASE_URL } from '@config';
import axios from 'axios';

export const SET_LOGIN_SUCCESS = 'SET_LOGIN_SUCCESS';
export const setLoginSuccess = () => ({
	type: SET_LOGIN_SUCCESS
});

export const SET_REGISTER_SUCCESS = 'SET_REGISTER_SUCCESS';
export const setRegisterSuccess = () => ({
	type: SET_REGISTER_SUCCESS
});

export const SET_TWO_FACTOR_CHALLENGE = 'SET_TWO_FACTOR_CHALLENGE';
export const setTwoFactorChallenge = challenge => ({
	type: SET_TWO_FACTOR_CHALLENGE,
	payload: challenge
});

export const SET_TWO_FACTOR_STATUS = 'SET_TWO_FACTOR_STATUS';
export const setTwoFactorStatus = ({ loading, error }) => ({
	type: SET_TWO_FACTOR_STATUS,
	payload: { loading, error }
});

export const CLEAR_TWO_FACTOR_CHALLENGE = 'CLEAR_TWO_FACTOR_CHALLENGE';
export const clearTwoFactorChallenge = () => ({
	type: CLEAR_TWO_FACTOR_CHALLENGE
});

export const SET_ERROR = 'SET_ERROR';
export const setError = error => ({
	type: SET_ERROR,
	payload: error
});

export const CLEAR_ERROR = 'CLEAR_ERROR ';
export const clearError = () => ({
	type: CLEAR_ERROR
});

export const CLEAR_AUTH = 'CLEAR_AUTH ';
export const clearAuth = () => ({
	type: CLEAR_AUTH
});

export const SET_ACTIVE_LDAP_SERVERS = 'GET_ACTIVE_LDAP_SERVERS';
export const setActiveLdapServers = servers => ({
	type: SET_ACTIVE_LDAP_SERVERS,
	payload: servers
});

export const SET_ACTIVE_OIDC_PROVIDERS = 'SET_ACTIVE_OIDC_PROVIDERS';
export const setActiveOidcProviders = (providers) => ({
	type: SET_ACTIVE_OIDC_PROVIDERS,
	payload: providers
});

export const getActiveOidcProviders = () => (dispatch) => {
	const url = `${API_BASE_URL}/api/openid-config/active`;
	return axios
		.get(url)
		.then(res => dispatch(setActiveOidcProviders(res.data)))
		.catch(err => dispatch(setError({ ...err?.response?.data, type: 'modal' })));
};

export const getActiveLdapServers = () => (dispatch) => {
	const url = `${API_BASE_URL}/api/ldap/servers/active`;
	const request = axios.get(url);
	return request
		.then(res => dispatch(setActiveLdapServers(res.data)))
		.catch(err => dispatch(setError({ ...err.data, type: 'modal' })));
};

const finishLogin = (dispatch, token) => {
	const { user } = decode(token);
	dispatch(setUserData(user));
	dispatch(setLoginSuccess());
};

// Sign-in shows no progress toast: every outcome has its own surface - the
// form's field errors, a modal, the two-step dialog, or the signed-in app.
export const submitLocalLogin = data => dispatch => {
	return jwtService
		.signInForLocal(data)
		.then(result => {
			if (result && result.twoFactorRequired) {
				// Password accepted; the server is holding the session until
				// the emailed code is verified (see submitTwoFactorCode).
				dispatch(setTwoFactorChallenge(result));
				return;
			}
			finishLogin(dispatch, result);
		})
		.catch(err => {
			let errObj = {};
			const status = err.status;

			if (status === 401) {
				errObj.code = status;
				errObj.message = 'Authentication Error';
				return dispatch(setError({ ...errObj, type: 'form' }));
			}

			// Show modal for org-related blocking errors (403)
			if (status === 403) {
				errObj = { ...err.data };
				return dispatch(setError({ ...errObj, type: 'modal' }));
			}

			errObj = { ...err.data };
			return dispatch(setError({ ...errObj, type: 'form' }));
		});
};

/**
 * Second step of a direct login: submit the emailed code for the pending
 * challenge. Errors stay inside the code dialog (auth.auth.twoFactor.error)
 * rather than the login form.
 */
export const submitTwoFactorCode = code => (dispatch, getState) => {
	const challenge = getState().auth.auth.twoFactor;
	if (!challenge) return Promise.resolve(false);
	dispatch(setTwoFactorStatus({ loading: true, error: null }));

	return jwtService
		.verifyTwoFactor({ challengeToken: challenge.challengeToken, code })
		.then(token => {
			dispatch(clearTwoFactorChallenge());
			finishLogin(dispatch, token);
			return true;
		})
		.catch(err => {
			const data = (err && err.data) || {};
			// Expired / exhausted challenge: back to the password form.
			if (data.code === 'TWO_FACTOR_CHALLENGE_INVALID' || data.code === 'TWO_FACTOR_TOO_MANY_ATTEMPTS') {
				dispatch(clearTwoFactorChallenge());
				dispatch(setError({ ...data, message: data.message || 'Please sign in again.', type: 'form' }));
				return false;
			}
			if (data.code && data.code.startsWith('ORGANIZATION') || data.code === 'LICENSE_EXPIRED' || data.code === 'LICENSE_CAPACITY_REACHED') {
				dispatch(clearTwoFactorChallenge());
				dispatch(setError({ ...data, type: 'modal' }));
				return false;
			}
			dispatch(setTwoFactorStatus({ loading: false, error: data.message || 'Invalid verification code.' }));
			return false;
		});
};

export const resendTwoFactorCode = () => (dispatch, getState) => {
	const challenge = getState().auth.auth.twoFactor;
	if (!challenge) return Promise.reject(new Error('No pending sign-in'));
	dispatch(setTwoFactorStatus({ loading: false, error: null }));

	return jwtService
		.resendTwoFactor({ challengeToken: challenge.challengeToken })
		.then(() => {
			dispatch(showMessage({ message: 'A new verification code has been sent.' }));
		})
		.catch(err => {
			const data = (err && err.data) || {};
			if (data.code === 'TWO_FACTOR_CHALLENGE_INVALID') {
				dispatch(clearTwoFactorChallenge());
				dispatch(setError({ ...data, message: data.message || 'Please sign in again.', type: 'form' }));
			} else {
				dispatch(setTwoFactorStatus({ loading: false, error: data.message || 'Could not resend the code.' }));
			}
			throw err;
		});
};

export const submitLdapLogin = data => dispatch => {
	return jwtService
		.signInForLdap(data)
		.then(token => finishLogin(dispatch, token))
		.catch(err => {
			let errObj = {};
			const status = err.response?.status;
			const data = err.response?.data;
			if (status === 401) {
				errObj.code = status;
				errObj.message = data.message ? data.message : 'Authentication Error.';
				return dispatch(setError({ ...errObj, type: 'modal' }));
			}

			// Show modal for org-related blocking errors (403)
			if (status === 403) {
				errObj = { ...data };
				return dispatch(setError({ ...errObj, type: 'modal' }));
			}

			errObj = { ...data };
			return dispatch(setError({ ...errObj, type: 'form' }));
		});

};

export const submitLoginForTrainingSession = (fullName, sessionCode, participantEmail, verificationToken) => dispatch => {
	return jwtService
		.signInForTrainingSession(fullName, sessionCode, participantEmail, verificationToken)
		.then(({ token, clusterStartup }) => {
			const { user } = decode(token);
			dispatch(setUserData(user));
			dispatch(setLoginSuccess());
			// Handed back to the caller rather than put in the store: only the
			// join screen cares, and only for the moments before the cluster is up.
			return { ok: true, clusterStartup };
		})
		.catch(err => {
			let errObj = {};
			if (err && err.status === 401) {
				errObj.code = err.status;
				errObj.message = 'Authentication Error';
			} else {
				errObj = { ...(err && err.data) };
			}
			if (!errObj.message) {
				errObj.message = 'Could not join the workshop session. Please try again.';
			}
			dispatch(setError({ ...errObj, type: 'form' }));
			// Falsy, so the join screen can stop its spinner. Returning the
			// dispatched action here made a failed sign-in look like a
			// successful one and the button span forever.
			return null;
		});
}

export const submitRegister = userData => dispatch => {
	dispatch(showMessage({ message: 'Creating User' }));
	return jwtService
		.createUser(userData)
		.then(token => {
			if (token) {
				const { user } = decode(token);
				dispatch(setUserData(user));
			}
			dispatch(setRegisterSuccess());
		})
		.catch(err => dispatch(setError({ ...err.data, type: 'form' })));
};

export const submitRegisterByAdmin = userData => dispatch => {
	dispatch(showMessage({ message: 'Creating User' }));
	return jwtService
		.createUserByAdmin(userData)
		.then(_ => {
			dispatch(showMessage({ message: `User created successfully` }));
			return true;
		})
		.catch(err => {
			dispatch(setError({ ...err.data, type: 'form' }));
			return false;
		});
};

export const createUser = userData => dispatch => {
	dispatch(showMessage({ message: 'Creating User' }));
	return axios
		.post(`${API_BASE_URL}/api/users/create`, userData)
		.then(() => {
			dispatch(showMessage({ message: 'User created successfully' }));
			return true;
		})
		.catch(err => {
			const errorData = err.response?.data || err.data || {};
			dispatch(setError({ ...errorData, type: 'form' }));
			return false;
		});
};
